import API_BASE from '../../utils/api';
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import ProfileDropdown from "../../components/ProfileDropdown";

const TeacherStudents = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewingStudent, setViewingStudent] = useState(null);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const classResponse = await axios.get(
        `${API_BASE}/classes/my-class`,
      );
      setClassInfo(classResponse.data);

      const studentsResponse = await axios.get(
        `${API_BASE}/classes/my-class/students`,
      );
      setStudents(studentsResponse.data);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const filteredStudents = students.filter(
    (student) =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.rollNumber.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-gray-100 flex items-center justify-center h-screen">
          <div className="text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 bg-gray-100 min-h-screen">
        
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">
              My Students
            </h1>
            <ProfileDropdown />
          </div>
        </nav>
        <div className="p-6">
          {/* Class Info Bar */}
          {classInfo && (
            <div className="bg-indigo-50 rounded-lg p-4 mb-6">
              <p className="text-indigo-700">
                <span className="font-semibold">Class:</span>{" "}
                {classInfo.className} - Section {classInfo.section} |
                <span className="font-semibold ml-2">Total Students:</span>{" "}
                {students.length}
              </p>
            </div>
          )}

          {/* Search Bar */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <input
              type="text"
              placeholder="Search students by name or roll number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border rounded-md px-4 py-2"
            />
          </div>

          {/* Students Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudents.map((student) => (
              <div
                key={student._id}
                className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition"
              >
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-3xl">👤</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{student.name}</h3>
                      <p className="text-sm text-gray-500">
                        Roll: {student.rollNumber}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Parent:</span>{" "}
                      {student.parentId?.name || "Not assigned"}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Contact:</span>{" "}
                      {student.phone || "N/A"}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Email:</span>{" "}
                      {student.email || "N/A"}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">DOB:</span>{" "}
                      {student.dateOfBirth
                        ? new Date(student.dateOfBirth).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>

                  <div className="mt-4 pt-4 border-t flex justify-end">
                    <button
                      onClick={() => setViewingStudent(student)}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      View Profile →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredStudents.length === 0 && (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <p className="text-gray-500">No students found</p>
            </div>
          )}
        </div>
      </div>

      {/* View Student Modal */}
      {viewingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl">
                  👤
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">{viewingStudent.name}</h2>
                  <p className="text-indigo-200 text-sm">Roll: {viewingStudent.rollNumber}</p>
                </div>
              </div>
              <button onClick={() => setViewingStudent(null)} className="text-white text-2xl hover:text-indigo-200">✕</button>
            </div>
            <div className="p-6 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Parent</p>
                <p className="font-semibold text-gray-800">{viewingStudent.parentId?.name || 'Not assigned'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Contact</p>
                <p className="font-semibold text-gray-800">{viewingStudent.phone || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-semibold text-gray-800">{viewingStudent.email || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Date of Birth</p>
                <p className="font-semibold text-gray-800">
                  {viewingStudent.dateOfBirth ? new Date(viewingStudent.dateOfBirth).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
            <div className="px-6 pb-4 flex justify-end">
              <button onClick={() => setViewingStudent(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherStudents;
